import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  HelpCircle, ChevronDown, Search, MessageCircle, Mail,
  Package, FileText, Clock, CreditCard, Shield
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';

const FAQPage = () => {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState('');
  const [openItems, setOpenItems] = useState({});
  const [activeCategory, setActiveCategory] = useState('all');

  const categories = [
    { id: 'all', name: t('faqPage.categories.all'), icon: HelpCircle },
    { id: 'shipping', name: t('faqPage.categories.shipping'), icon: Package },
    { id: 'pricing', name: t('faqPage.categories.pricing'), icon: CreditCard },
    { id: 'tracking', name: t('faqPage.categories.tracking'), icon: Clock },
    { id: 'customs', name: t('faqPage.categories.customs'), icon: FileText },
    { id: 'insurance', name: t('faqPage.categories.insurance'), icon: Shield },
  ];

  const faqs = [
    // Shipping
    {
      category: 'shipping',
      questionKey: 'cargoTypes',
      question: t('faqPage.faqs.cargoTypes.question'),
      answer: t('faqPage.faqs.cargoTypes.answer')
    },
    {
      category: 'shipping',
      questionKey: 'fclVsLcl',
      question: t('faqPage.faqs.fclVsLcl.question'),
      answer: t('faqPage.faqs.fclVsLcl.answer')
    },
    {
      category: 'shipping',
      questionKey: 'transitTime',
      question: t('faqPage.faqs.transitTime.question'),
      answer: t('faqPage.faqs.transitTime.answer')
    },
    {
      category: 'shipping',
      questionKey: 'doorToDoor',
      question: t('faqPage.faqs.doorToDoor.question'),
      answer: t('faqPage.faqs.doorToDoor.answer')
    },
    // Pricing
    {
      category: 'pricing',
      questionKey: 'ratesCalculation',
      question: t('faqPage.faqs.ratesCalculation.question'),
      answer: t('faqPage.faqs.ratesCalculation.answer')
    },
    {
      category: 'pricing',
      questionKey: 'incoterms',
      question: t('faqPage.faqs.incoterms.question'),
      answer: t('faqPage.faqs.incoterms.answer')
    },
    {
      category: 'pricing',
      questionKey: 'additionalCosts',
      question: t('faqPage.faqs.additionalCosts.question'),
      answer: t('faqPage.faqs.additionalCosts.answer')
    },
    // Tracking
    {
      category: 'tracking',
      questionKey: 'howToTrack',
      question: t('faqPage.faqs.howToTrack.question'),
      answer: t('faqPage.faqs.howToTrack.answer')
    },
    {
      category: 'tracking',
      questionKey: 'trackingFrequency',
      question: t('faqPage.faqs.trackingFrequency.question'),
      answer: t('faqPage.faqs.trackingFrequency.answer')
    },
    // Customs
    {
      category: 'customs',
      questionKey: 'documentsNeeded',
      question: t('faqPage.faqs.documentsNeeded.question'),
      answer: t('faqPage.faqs.documentsNeeded.answer')
    },
    {
      category: 'customs',
      questionKey: 'customsHelp',
      question: t('faqPage.faqs.customsHelp.question'),
      answer: t('faqPage.faqs.customsHelp.answer')
    },
    // Insurance
    {
      category: 'insurance',
      questionKey: 'cargoInsured',
      question: t('faqPage.faqs.cargoInsured.question'),
      answer: t('faqPage.faqs.cargoInsured.answer')
    },
    {
      category: 'insurance',
      questionKey: 'insuranceCoverage',
      question: t('faqPage.faqs.insuranceCoverage.question'),
      answer: t('faqPage.faqs.insuranceCoverage.answer')
    },
  ];

  const toggleItem = (questionKey) => {
    setOpenItems(prev => ({
      ...prev,
      [questionKey]: !prev[questionKey]
    }));
  };

  const filteredFaqs = faqs.filter(faq => {
    const matchesCategory = activeCategory === 'all' || faq.category === activeCategory;
    const matchesSearch = searchTerm === '' || 
      faq.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
      faq.answer.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  return (
    <div className="min-h-screen bg-steel-50" data-testid="faq-page">
      {/* Hero Section */}
      <section className="bg-navy-900 pt-32 pb-20">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl mx-auto text-center"
          >
            <span className="inline-block px-4 py-2 bg-gold/10 border border-gold/30 rounded-full text-gold text-sm font-medium tracking-wider uppercase mb-6">
              {t('faqPage.badge')}
            </span>
            <h1 className="font-outfit text-4xl md:text-5xl font-bold text-white mb-6">
              {t('faqPage.title')}
            </h1>
            <p className="text-steel-300 text-lg mb-10">
              {t('faqPage.subtitle')}
            </p>
            
            {/* Search */}
            <div className="relative max-w-xl mx-auto">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-steel-400" />
              <Input
                placeholder={t('faqPage.searchPlaceholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-12 h-14 text-base bg-white/10 border-white/20 text-white placeholder:text-steel-400"
              />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Categories */}
      <section className="py-8 bg-white border-b border-steel-200">
        <div className="container-custom">
          <div className="flex flex-wrap justify-center gap-3">
            {categories.map(cat => (
              <button
                key={cat.id}
                onClick={() => setActiveCategory(cat.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-full font-medium transition-all ${
                  activeCategory === cat.id
                    ? 'bg-navy-800 text-white'
                    : 'bg-steel-100 text-steel-600 hover:bg-steel-200'
                }`}
              >
                <cat.icon className="w-4 h-4" />
                {cat.name}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ List */}
      <section className="py-16">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto">
            {filteredFaqs.length === 0 ? (
              <div className="text-center py-12">
                <HelpCircle className="w-12 h-12 text-steel-300 mx-auto mb-4" />
                <p className="text-steel-600">
                  {t('faqPage.noQuestions')}
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {filteredFaqs.map((faq) => (
                  <motion.div
                    key={faq.questionKey}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                  >
                    <Card className="border-0 shadow-md overflow-hidden">
                      <button
                        onClick={() => toggleItem(faq.questionKey)}
                        className="w-full p-6 flex items-start justify-between text-left hover:bg-steel-50 transition-colors"
                      >
                        <span className="font-outfit font-semibold text-navy-800 pr-4">
                          {faq.question}
                        </span>
                        <ChevronDown className={`w-5 h-5 text-gold flex-shrink-0 transition-transform ${
                          openItems[faq.questionKey] ? 'rotate-180' : ''
                        }`} />
                      </button>
                      <AnimatePresence>
                        {openItems[faq.questionKey] && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.3 }}
                          >
                            <CardContent className="pt-0 px-6 pb-6">
                              <p className="text-steel-600 leading-relaxed">
                                {faq.answer}
                              </p>
                            </CardContent>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </Card>
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-16 bg-navy-900">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="font-outfit text-3xl font-bold text-white mb-4">
              {t('faqPage.didntFindAnswer')}
            </h2>
            <p className="text-steel-300 mb-8">
              {t('faqPage.teamAvailable')}
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/contact">
                <Button className="btn-accent h-12 px-8">
                  <MessageCircle className="w-4 h-4 mr-2" />
                  {t('faqPage.contactUs')}
                </Button>
              </Link>
              <a href="mailto:info@elementarycargo.com">
                <Button variant="outline" className="h-12 px-8 border-white/30 text-white hover:bg-white/10">
                  <Mail className="w-4 h-4 mr-2" />
                  info@elementarycargo.com
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FAQPage;
